import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminApi } from '../hooks/useAdminApi';
import { PaymentButton } from './PaymentPage';
import { 
  Settings, 
  Building2,
  CreditCard,
  Users,
  Bell,
  Shield,
  LogOut,
  Crown,
  Tag,
  Check,
  Wrench,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  X,
  Clock,
  AlertTriangle,
  Download,
  Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, organization, token, logout, isAtelier, isMechanic } = useAdminAuth();
  const { get, post } = useAdminApi();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  
  // Mechanic management state (LEGACY - conserve pour rétro-compat de l'API sous-jacente)
  const [mechanics, setMechanics] = useState([]);

  // INTERNAL-USERS-V5 - Gestion unifiée des utilisateurs internes
  const [internalUsers, setInternalUsers] = useState([]);
  const [modulesMeta, setModulesMeta] = useState({ modules: [], roles: [], role_defaults: {} });
  const [showInternalForm, setShowInternalForm] = useState(false);
  const [internalForm, setInternalForm] = useState({
    email: '', first_name: '', last_name: '', role: 'admin_repartition',
    access_modules: [], phone: '', password: ''
  });
  const [internalSaving, setInternalSaving] = useState(false);
  const [tempPasswordDisplay, setTempPasswordDisplay] = useState(null); // { email, password }
  const [editModulesFor, setEditModulesFor] = useState(null); // { user, modules[] }
  
  const isAdmin = user?.role && !['atelier', 'mechanic'].includes(user.role);

  useEffect(() => {
    get('/saas/plans').then(res => {
      if (res.success) setPlans(res.data.filter(p => !p.is_free && !p.is_addon));
    });
    get('/saas/subscription/current').then(res => {
      if (res.success) {
        setSubscription(res.data);
        setPendingChange(res.data?.pending_plan_change || null);
      }
    });
  }, [get]);

  // Fetch mechanic users
  const fetchMechanics = useCallback(async () => {
    if (!token || (!isAdmin && !isAtelier)) return;
    try {
      const usersRes = await axios.get(`${API_URL}/api/mechanic/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setMechanics(usersRes.data);
    } catch {}
  }, [token, isAdmin, isAtelier]);

  useEffect(() => {
    fetchMechanics();
  }, [fetchMechanics]);

  const handleCreateMechanic = null; // deprecie - remplace par handleCreateInternalUser
  const handleDeleteMechanic = null;

  // INTERNAL-USERS-V5 handlers
  const fetchInternalUsers = useCallback(async () => {
    if (!token || !isAdmin) return;
    try {
      const [listRes, metaRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/internal-users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/admin/internal-users/known-modules`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInternalUsers(listRes.data);
      setModulesMeta(metaRes.data);
    } catch (e) { /* silent */ }
  }, [token, isAdmin]);

  useEffect(() => { fetchInternalUsers(); }, [fetchInternalUsers]);

  // Pre-cocher les modules par defaut quand le role change
  useEffect(() => {
    const defaults = modulesMeta.role_defaults?.[internalForm.role] || [];
    setInternalForm(f => ({ ...f, access_modules: defaults }));
  }, [internalForm.role, modulesMeta.role_defaults]);

  const handleCreateInternalUser = async (e) => {
    e.preventDefault();
    if (!internalForm.email || !internalForm.first_name || !internalForm.last_name || !internalForm.role) {
      toast.error('Prenom, nom, courriel et role sont requis');
      return;
    }
    setInternalSaving(true);
    try {
      const payload = {
        email: internalForm.email.trim(),
        first_name: internalForm.first_name.trim(),
        last_name: internalForm.last_name.trim(),
        role: internalForm.role,
        access_modules: internalForm.access_modules,
        phone: internalForm.phone || null,
      };
      if (internalForm.password) payload.password = internalForm.password;
      const res = await axios.post(`${API_URL}/api/admin/internal-users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Compte cree pour ${payload.first_name} ${payload.last_name}`);
      if (res.data.temp_password) {
        setTempPasswordDisplay({ email: payload.email, password: res.data.temp_password });
      }
      setInternalForm({ email: '', first_name: '', last_name: '', role: 'admin_repartition', access_modules: [], phone: '', password: '' });
      setShowInternalForm(false);
      fetchInternalUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation');
    } finally {
      setInternalSaving(false);
    }
  };

  const handleToggleModule = (mod) => {
    setInternalForm(f => ({
      ...f,
      access_modules: f.access_modules.includes(mod)
        ? f.access_modules.filter(m => m !== mod)
        : [...f.access_modules, mod]
    }));
  };

  const handleToggleActive = async (u) => {
    if (!window.confirm(`${u.is_active ? 'Desactiver' : 'Reactiver'} ${u.first_name} ${u.last_name} ?`)) return;
    try {
      if (u.is_active) {
        await axios.delete(`${API_URL}/api/admin/internal-users/${u.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.patch(`${API_URL}/api/admin/internal-users/${u.id}`,
          { is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`Utilisateur ${u.is_active ? 'desactive' : 'reactive'}`);
      fetchInternalUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPasswordInternal = async (u) => {
    if (!window.confirm(`Reinitialiser le mot de passe de ${u.first_name} ${u.last_name} ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/internal-users/${u.id}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTempPasswordDisplay({ email: u.email, password: res.data.temp_password });
      toast.success('Mot de passe reinitialise');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleSaveModulesEdit = async () => {
    if (!editModulesFor) return;
    try {
      await axios.patch(`${API_URL}/api/admin/internal-users/${editModulesFor.user.id}`,
        { access_modules: editModulesFor.modules, role: editModulesFor.user.role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Modules mis a jour');
      setEditModulesFor(null);
      fetchInternalUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Copie'),
      () => toast.error('Impossible de copier')
    );
  };

  const roleLabels = {
    admin_entretiens: 'Admin Entretiens',
    admin_repartition: 'Repartition',
    admin_facturation: 'Facturation',
    super_admin: 'Super Admin',
    atelier: 'Atelier',
    mechanic: 'Mecanicien',
    agent_ventes: 'Agent de ventes',
    service_client: 'Service Client',
  };

  const moduleLabels = {
    // Nouvelles cles page_* (V6 - permissions granulaires par sidebar)
    page_dashboard:      'Tableau de bord',
    page_drivers:        'Chauffeurs',
    page_map:            'Carte',
    page_dispatch:       'Repartition',
    page_service_client: 'Service Client',
    page_agent_ventes:   'Nouvelle commande',
    page_messages:       'Messagerie',
    page_flotte:         'Flotte',
    page_entretiens:     'Entretiens',
    page_atelier:        'Atelier',
    page_portail_clients:'Portail Clients',
    page_facturation:    'Facturation',
    page_comptabilite:   'Comptabilite',
    page_analytics:      'Analytics',
    page_settings:       'Parametres',
    // Legacy (retrocompat - deja stockes chez certains users)
    module_entretiens: 'Entretiens (legacy)',
    module_repartition: 'Repartition (legacy)',
    module_facturation: 'Facturation (legacy)',
    module_logistique_plus: 'Logistique Plus (legacy)',
    module_messagerie: 'Messagerie (legacy)',
    module_comptabilite: 'Comptabilite (legacy)',
  };

  const roleColors = {
    admin_entretiens: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    admin_repartition: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    admin_facturation: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    service_client: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
    agent_ventes: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
    mechanic: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    atelier: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Settings className="text-amber-500" />
          Paramètres
        </h1>
        <p className="text-slate-400">Gérez votre compte et votre organisation</p>
      </div>

      {/* Profile Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users size={20} />
          Mon profil
        </h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center text-2xl font-bold text-black">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div>
            <p className="text-white font-semibold text-lg">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-slate-400">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 bg-amber-500/20 text-amber-500 rounded text-xs">
              {roleLabels[user?.role] || user?.role}
            </span>
          </div>
        </div>
      </div>

      {/* Organization Card */}
      {organization && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Building2 size={20} />
            Organisation
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-slate-500 text-sm">Nom</p>
              <p className="text-white">{organization.name}</p>
            </div>
            <div>
              <p className="text-slate-500 text-sm">Courriel</p>
              <p className="text-white">{organization.email}</p>
            </div>
            {organization.phone && (
              <div>
                <p className="text-slate-500 text-sm">Téléphone</p>
                <p className="text-white">{organization.phone}</p>
              </div>
            )}
            {organization.address && (
              <div>
                <p className="text-slate-500 text-sm">Adresse</p>
                <p className="text-white">
                  {organization.address}
                  {organization.city && `, ${organization.city}`}
                  {organization.province && `, ${organization.province}`}
                  {organization.postal_code && ` ${organization.postal_code}`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Subscription & Payment Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="subscription-section">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CreditCard size={20} />
          Abonnement
        </h2>

        {/* Current plan info */}
        {organization?.subscription_plan && (
          <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg mb-4">
            <div>
              <p className="text-white font-medium flex items-center gap-2">
                <Crown size={16} className="text-amber-500" />
                {subscription?.subscription?.plan_name || 
                 (organization.subscription_plan === 'plan_pro' ? 'ProPilote Pro' : 
                  plans.find(p => p.id === organization.subscription_plan)?.name || 
                  organization.subscription_plan)}
              </p>
              {organization.promo_code && (
                <p className="text-xs text-green-400 flex items-center gap-1 mt-1">
                  <Tag size={12} /> Code promo: {organization.promo_code}
                </p>
              )}
              {subscription?.trial_days_remaining != null && subscription.trial_days_remaining > 0 && (
                <p className="text-xs text-amber-400 mt-1">
                  Essai gratuit — {subscription.trial_days_remaining} jour(s) restant(s)
                </p>
              )}
            </div>
            <span className={`text-sm font-medium ${subscription?.is_active ? 'text-green-400' : 'text-red-400'}`}>
              {subscription?.is_active ? 'Actif' : 'Expire'}
            </span>
          </div>
        )}

        {/* Active modules */}
        {organization?.active_modules?.length > 0 ? (
          <div className="space-y-2 mb-4">
            {organization.active_modules.map(mod => (
              <div key={mod} className="flex items-center gap-2 text-sm">
                <Check size={14} className="text-green-400" />
                <span className="text-slate-300">
                  {mod === 'module_entretiens' ? 'Suivi des Entretiens' :
                   mod === 'module_repartition' ? 'Repartition' :
                   mod === 'module_facturation' ? 'Facturation' : mod}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 text-sm mb-4">Aucun module actif — essai gratuit en cours</p>
        )}

        {/* Promo code section */}
        {!organization?.promo_code && (
          <div className="border-t border-slate-800 pt-4 mt-4">
            <p className="text-sm text-slate-400 mb-2">Code promo</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none uppercase"
                placeholder="VOTRE-CODE"
                data-testid="settings-promo-input"
              />
              <button
                onClick={async () => {
                  setPromoLoading(true);
                  const res = await post('/saas/promo/apply', { code: promoCode });
                  if (res.success) {
                    setPromoResult({ success: true, message: res.data.message });
                    setTimeout(() => window.location.reload(), 1500);
                  } else {
                    setPromoResult({ success: false, message: res.error });
                  }
                  setPromoLoading(false);
                }}
                disabled={promoLoading || !promoCode.trim()}
                className="px-4 py-2 bg-amber-500/20 text-amber-500 rounded-lg text-sm hover:bg-amber-500/30 disabled:opacity-50 transition-colors"
                data-testid="apply-promo-btn"
              >
                {promoLoading ? '...' : 'Appliquer'}
              </button>
            </div>
            {promoResult && (
              <p className={`text-xs mt-2 ${promoResult.success ? 'text-green-400' : 'text-red-400'}`}>
                {promoResult.message}
              </p>
            )}
          </div>
        )}

        {/* Upgrade / Downgrade Plan System */}
        <div className="border-t border-slate-800 pt-4 mt-4">
          <p className="text-sm text-white font-semibold mb-3">Changer de forfait</p>

          {/* Pending downgrade banner */}
          {pendingChange && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="pending-downgrade-banner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-amber-400" />
                  <p className="text-sm text-amber-300">
                    Passage a <strong>{pendingChange.new_plan_name}</strong> prevu le {pendingChange.scheduled_at?.substring(0, 10)}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await axios.delete(`${API_URL}/api/saas/subscriptions/cancel-downgrade`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      setPendingChange(null);
                      toast.success('Changement de plan annule');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur');
                    }
                  }}
                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
                  data-testid="cancel-downgrade-btn"
                >
                  <X size={12} /> Annuler
                </button>
              </div>
            </div>
          )}
            
          {/* Plan grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {plans.filter(p => !p.is_addon).map(plan => {
              const currentPlanId = subscription?.subscription?.plan_id;
              const currentTier = plans.find(p => p.id === currentPlanId)?.tier || 0;
              const isCurrent = plan.id === currentPlanId;
              const isUpgrade = plan.tier > currentTier;
              const isDowngrade = plan.tier < currentTier && plan.tier > 0;
              const isSameTier = plan.tier === currentTier && !isCurrent;
              const isSelected = selectedUpgrade === plan.slug;
              const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
              const isPendingTarget = pendingChange?.new_plan_id === plan.id;
              
              return (
                <button
                  key={plan.id}
                  onClick={() => {
                    if (!isCurrent && !isPendingTarget) setSelectedUpgrade(isSelected ? null : plan.slug);
                  }}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    isCurrent
                      ? 'border-green-500/30 bg-green-500/10 cursor-default'
                      : isPendingTarget
                        ? 'border-amber-500/30 bg-amber-500/5 cursor-default'
                        : isSelected
                          ? 'border-amber-500 bg-amber-500/10'
                          : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                  }`}
                  data-testid={`upgrade-plan-${plan.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-white">{plan.name}</span>
                    {isCurrent && <span className="text-xs text-green-400 font-medium flex items-center gap-1"><Check size={12} /> Actuel</span>}
                    {!isCurrent && !isPendingTarget && isUpgrade && (
                      <span className="text-xs text-blue-400 font-medium flex items-center gap-1"><ArrowUp size={12} /> Upgrade</span>
                    )}
                    {!isCurrent && !isPendingTarget && isDowngrade && (
                      <span className="text-xs text-orange-400 font-medium flex items-center gap-1"><ArrowDown size={12} /> Downgrade</span>
                    )}
                    {isPendingTarget && (
                      <span className="text-xs text-amber-400 font-medium flex items-center gap-1"><Clock size={12} /> En attente</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{plan.description?.substring(0, 60)}</p>
                  <p className="text-sm font-bold text-white mt-1">
                    {price}$ <span className="text-slate-500 text-xs font-normal">/{billingCycle === 'yearly' ? 'an' : 'mois'}</span>
                  </p>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 mb-3">
            <span className={`text-xs ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>Mensuel</span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`w-10 h-5 rounded-full relative transition-colors ${billingCycle === 'yearly' ? 'bg-amber-500' : 'bg-slate-700'}`}
              data-testid="settings-billing-toggle"
            >
              <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${billingCycle === 'yearly' ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
            <span className={`text-xs ${billingCycle === 'yearly' ? 'text-white' : 'text-slate-500'}`}>
              Annuel <span className="text-green-400">-20%</span>
            </span>
          </div>

          {selectedUpgrade && (() => {
            const selectedPlan = plans.find(p => p.slug === selectedUpgrade);
            const currentPlanId = subscription?.subscription?.plan_id;
            const currentTier = plans.find(p => p.id === currentPlanId)?.tier || 0;
            const isUpgrade = selectedPlan?.tier > currentTier;
            const isDowngrade = selectedPlan?.tier < currentTier;
            const isTrial = subscription?.subscription?.status === 'trialing';
            const selectedPrice = billingCycle === 'yearly' ? selectedPlan?.price_yearly : selectedPlan?.price_monthly;
            const currentPrice = subscription?.subscription?.price || 0;

            return (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3" data-testid="plan-change-details">
                {isTrial && (
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-300">
                      Votre essai gratuit continuera sur le plan <strong>{selectedPlan?.name}</strong> jusqu'a la meme date de fin.
                    </p>
                  </div>
                )}
                {isUpgrade && !isTrial && (
                  <div className="flex items-start gap-2">
                    <ArrowUp size={16} className="text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-300">
                      Upgrade immediat. Un credit au prorata de votre plan actuel ({currentPrice}$) sera applique sur le nouveau ({selectedPrice}$).
                    </p>
                  </div>
                )}
                {isDowngrade && !isTrial && (
                  <div className="flex items-start gap-2">
                    <ArrowDown size={16} className="text-orange-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-orange-300">
                      Vous conserverez votre plan actuel jusqu'a la fin de la periode en cours. Le passage a {selectedPlan?.name} ({selectedPrice}$/mois) prendra effet automatiquement apres.
                    </p>
                  </div>
                )}

                <button
                  onClick={async () => {
                    setChangingPlan(true);
                    try {
                      const res = await axios.post(`${API_URL}/api/saas/subscriptions/change-plan`, {
                        new_plan_id: selectedPlan?.id,
                        billing_cycle: billingCycle,
                      }, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const data = res.data;
                      if (data.type === 'upgrade' || data.type === 'trial_switch') {
                        toast.success(data.message);
                        setTimeout(() => window.location.reload(), 1500);
                      } else if (data.type === 'downgrade') {
                        toast.success(data.message);
                        setPendingChange({
                          new_plan_id: selectedPlan?.id,
                          new_plan_name: selectedPlan?.name,
                          scheduled_at: data.effective_date,
                        });
                        setSelectedUpgrade(null);
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur lors du changement de plan');
                    } finally {
                      setChangingPlan(false);
                    }
                  }}
                  disabled={changingPlan}
                  className={`w-full py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    isUpgrade || isTrial
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                  } disabled:opacity-50`}
                  data-testid="confirm-plan-change-btn"
                >
                  {changingPlan ? 'Traitement...' : (
                    isUpgrade || isTrial
                      ? `Passer a ${selectedPlan?.name} maintenant`
                      : `Programmer le passage a ${selectedPlan?.name}`
                  )}
                </button>
              </div>
            );
          })()}
          {!selectedUpgrade && !pendingChange && (
            <p className="text-xs text-slate-500">Selectionnez un forfait ci-dessus pour changer de plan</p>
          )}
        </div>
      </div>

      {/* INTERNAL-USERS-V5 - Gestion utilisateurs internes (admin uniquement) */}
      {isAdmin && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="internal-users-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users size={20} className="text-amber-500" />
              Utilisateurs internes
            </h2>
            <button
              onClick={() => setShowInternalForm(!showInternalForm)}
              className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors"
              data-testid="add-internal-user-btn"
            >
              <Plus size={14} /> Ajouter un employe
            </button>
          </div>

          {showInternalForm && (
            <form onSubmit={handleCreateInternalUser} className="bg-slate-800 rounded-lg p-4 mb-4 space-y-3" data-testid="internal-user-create-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Prenom *</label>
                  <input type="text" required value={internalForm.first_name}
                    onChange={e => setInternalForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                    placeholder="Nathalie" data-testid="internal-firstname" />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nom *</label>
                  <input type="text" required value={internalForm.last_name}
                    onChange={e => setInternalForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                    placeholder="Tremblay" data-testid="internal-lastname" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Courriel *</label>
                <input type="email" required value={internalForm.email}
                  onChange={e => setInternalForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                  placeholder="nathalie@entreprise.ca" data-testid="internal-email" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Telephone (optionnel)</label>
                <input type="tel" value={internalForm.phone}
                  onChange={e => setInternalForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                  placeholder="514-555-0123" data-testid="internal-phone" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Role principal *</label>
                <select required value={internalForm.role}
                  onChange={e => setInternalForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                  data-testid="internal-role">
                  {(modulesMeta.roles || []).map(r => (
                    <option key={r} value={r}>{roleLabels[r] || r}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Determine la page d&apos;accueil et le menu principal de l&apos;utilisateur</p>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-2">Modules autorises (1 case = 1 section du menu)</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(modulesMeta.modules || []).map(mod => (
                    <label key={mod} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${internalForm.access_modules.includes(mod) ? 'bg-amber-500/10 border-amber-500/40' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}>
                      <input type="checkbox" checked={internalForm.access_modules.includes(mod)}
                        onChange={() => handleToggleModule(mod)}
                        className="accent-amber-500"
                        data-testid={`internal-module-${mod}`} />
                      <span className="text-xs text-slate-200">{moduleLabels[mod] || mod}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">Coche pour donner acces. Auto-remplis selon le role (modifiable).</p>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Mot de passe (laisse vide pour generer)</label>
                <input type="text" value={internalForm.password}
                  onChange={e => setInternalForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500/50 focus:outline-none"
                  placeholder="Optionnel - genere automatiquement si vide" data-testid="internal-password" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={internalSaving}
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                  data-testid="submit-internal-user-btn">
                  {internalSaving ? 'Creation...' : 'Creer le compte'}
                </button>
                <button type="button"
                  onClick={() => { setShowInternalForm(false); setInternalForm({ email: '', first_name: '', last_name: '', role: 'admin_repartition', access_modules: [], phone: '', password: '' }); }}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          )}

          {tempPasswordDisplay && (
            <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-4 mb-4" data-testid="temp-password-display">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-emerald-400 font-semibold text-sm flex items-center gap-2">
                  <Check size={16} /> Mot de passe temporaire cree
                </div>
                <button onClick={() => setTempPasswordDisplay(null)} className="text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-2">Communique ces identifiants a l&apos;utilisateur. Ce mot de passe ne sera plus affiche.</p>
              <div className="space-y-1 font-mono text-xs">
                <div className="flex items-center justify-between bg-slate-900 rounded p-2">
                  <span className="text-slate-300">Email : <span className="text-white">{tempPasswordDisplay.email}</span></span>
                  <button onClick={() => copyToClipboard(tempPasswordDisplay.email)} className="text-emerald-400 hover:text-emerald-300 text-[10px] uppercase">Copier</button>
                </div>
                <div className="flex items-center justify-between bg-slate-900 rounded p-2">
                  <span className="text-slate-300">Mdp : <span className="text-white">{tempPasswordDisplay.password}</span></span>
                  <button onClick={() => copyToClipboard(tempPasswordDisplay.password)} className="text-emerald-400 hover:text-emerald-300 text-[10px] uppercase">Copier</button>
                </div>
              </div>
            </div>
          )}

          {internalUsers.length === 0 ? (
            <div className="text-center py-6">
              <Users size={28} className="mx-auto text-slate-600 mb-2" />
              <p className="text-slate-500 text-sm">Aucun utilisateur interne</p>
              <p className="text-slate-600 text-xs mt-1">Ajoute un employe pour lui donner acces aux modules de ton choix</p>
            </div>
          ) : (
            <div className="space-y-2">
              {internalUsers.map(u => {
                const roleCls = roleColors[u.role] || 'text-slate-400 bg-slate-500/10 border-slate-500/30';
                return (
                  <div key={u.id}
                    className={`p-3 bg-slate-800 rounded-lg border ${u.is_active ? 'border-slate-700' : 'border-red-500/20 opacity-60'}`}
                    data-testid={`internal-user-row-${u.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 font-semibold text-xs shrink-0">
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {u.first_name} {u.last_name}
                            {!u.is_active && <span className="ml-2 text-[10px] text-red-400">(inactif)</span>}
                          </p>
                          <p className="text-slate-400 text-xs truncate">{u.email}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${roleCls} shrink-0`}>{roleLabels[u.role] || u.role}</span>
                    </div>
                    <div className="flex items-center flex-wrap gap-1 mb-2">
                      {(u.access_modules || []).map(m => (
                        <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-slate-300 border border-slate-700">
                          {moduleLabels[m] || m}
                        </span>
                      ))}
                      {(u.access_modules || []).length === 0 && (
                        <span className="text-[10px] italic text-slate-500">Aucun module</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setEditModulesFor({ user: u, modules: [...(u.access_modules || [])] })}
                        className="text-[11px] px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
                        data-testid={`edit-modules-${u.id}`}>
                        Modifier modules
                      </button>
                      <button
                        onClick={() => handleResetPasswordInternal(u)}
                        className="text-[11px] px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-300"
                        data-testid={`reset-password-${u.id}`}>
                        Reinit. mdp
                      </button>
                      <button
                        onClick={() => handleToggleActive(u)}
                        className={`text-[11px] px-2 py-1 rounded ${u.is_active ? 'bg-red-500/20 hover:bg-red-500/30 text-red-300' : 'bg-green-500/20 hover:bg-green-500/30 text-green-300'}`}
                        data-testid={`toggle-active-${u.id}`}>
                        {u.is_active ? 'Desactiver' : 'Reactiver'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {editModulesFor && (
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
              onClick={() => setEditModulesFor(null)} data-testid="edit-modules-modal">
              <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-md w-full"
                onClick={e => e.stopPropagation()}>
                <h3 className="text-white font-semibold mb-1">Modules pour {editModulesFor.user.first_name} {editModulesFor.user.last_name}</h3>
                <p className="text-slate-400 text-xs mb-4">Coche les modules auxquels cet utilisateur doit avoir acces.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                  {(modulesMeta.modules || []).map(mod => {
                    const checked = editModulesFor.modules.includes(mod);
                    return (
                      <label key={mod} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-amber-500/10 border-amber-500/40' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setEditModulesFor(state => ({
                            ...state,
                            modules: checked ? state.modules.filter(m => m !== mod) : [...state.modules, mod]
                          }))}
                          className="accent-amber-500" />
                        <span className="text-xs text-slate-200">{moduleLabels[mod] || mod}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditModulesFor(null)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md text-sm">
                    Annuler
                  </button>
                  <button onClick={handleSaveModulesEdit}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-md text-sm font-medium"
                    data-testid="save-modules-btn">
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-600 mt-3">
            Les utilisateurs se connectent sur /admin/login (ou /atelier/login pour mecaniciens/atelier) avec leur courriel et mot de passe.
          </p>

        </div>
      )}

            {/* Payment History */}
      {(isAdmin || isAtelier) && <PaymentHistorySection token={token} />}

      {/* Logout */}
      <button
        onClick={logout}
        className="w-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        <LogOut size={20} />
        Se déconnecter
      </button>
    </div>
  );
};


function PaymentHistorySection({ token }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/saas/payment/history`, { headers })
      .then(r => setTransactions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const downloadReceipt = async (txId) => {
    try {
      const res = await axios.get(`${API_URL}/api/saas/payment/receipt/${txId}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur telechargement recu');
    }
  };

  const STATUS_MAP = {
    paid: { label: 'Paye', cls: 'bg-green-500/20 text-green-400' },
    initiated: { label: 'Initie', cls: 'bg-slate-700 text-slate-300' },
    pending: { label: 'En cours', cls: 'bg-amber-500/20 text-amber-400' },
    failed: { label: 'Echoue', cls: 'bg-red-500/20 text-red-400' },
    cancelled: { label: 'Annule', cls: 'bg-slate-700 text-slate-400' },
  };

  if (loading) return null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="payment-history-section">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <CreditCard size={20} className="text-amber-500" />
        Historique des paiements
      </h2>

      {transactions.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Aucune transaction</p>
      ) : (
        <div className="space-y-2">
          {transactions.map(tx => {
            const st = STATUS_MAP[tx.payment_status] || STATUS_MAP.initiated;
            const date = tx.created_at ? tx.created_at.slice(0, 10) : '';
            const cycle = tx.billing_cycle === 'yearly' ? 'Annuel' : 'Mensuel';
            return (
              <div key={tx.id}
                className="flex items-center justify-between p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-slate-600 transition-colors"
                data-testid={`payment-tx-${tx.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{tx.plan_name || tx.plan_slug}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {date} — {cycle} — {(tx.provider || '').charAt(0).toUpperCase() + (tx.provider || '').slice(1)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold text-sm">{(tx.amount || 0).toFixed(2)} $</span>
                  {tx.payment_status === 'paid' && (
                    <button
                      onClick={() => downloadReceipt(tx.id)}
                      className="p-1.5 text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                      title="Telecharger le recu PDF"
                      data-testid={`download-receipt-${tx.id}`}
                    >
                      <Download size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
